import React, { useEffect, useState, useContext, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { AppContext } from '../AppContext';
import './SearchAnimation.css';
import searchingSound from '../assets/audio/searching.mp3';
import foundSound from '../assets/audio/found.mp3';

function SearchAnimation({ position, onComplete, result }) {
  const [stage, setStage] = useState('searching');
  const { isDarkMode } = useContext(AppContext);
  const searchingAudioRef = useRef(null);
  const foundAudioRef = useRef(null);

  console.log('SearchAnimation rendered with isDarkMode:', isDarkMode);

  // Play searching sound when component mounts
  useEffect(() => {
    if (searchingAudioRef.current) {
      searchingAudioRef.current.play().catch(err => console.log('Audio play failed:', err));
    }
  }, []);

  useEffect(() => {
    // If result is still searching, stay in searching state
    if (result.searching) {
      return;
    }

    // Once we have actual results, transition after animation
    const searchTimer = setTimeout(() => {
      if (result.success) {
        setStage('found');
        // Play found sound
        if (foundAudioRef.current) {
          foundAudioRef.current.play().catch(err => console.log('Audio play failed:', err));
        }
        // Show found animation, then complete
        setTimeout(() => {
          onComplete();
        }, 3000); // Extended from 2000ms to 3000ms
      } else {
        setStage('notFound');
        // Show not found animation, then complete
        setTimeout(() => {
          onComplete();
        }, 1500); // Reduced to allow faster next search
      }
    }, 2000); // Extended from 1500ms to 2000ms - dust clearing animation duration

    return () => clearTimeout(searchTimer);
  }, [result, onComplete]);

  if (!position) return null;

  return (
    <Box
      className="search-animation-container"
      sx={{
        position: 'fixed',
        left: `${position.x}px`,
        top: `${position.y}px`,
        transform: 'translate(-50%, -50%)',
        pointerEvents: 'none',
        zIndex: 1000,
      }}
    >
      {/* Ripple effect */}
      {stage === 'searching' && (
        <Box
          className="search-ripple"
          sx={{
            borderColor: `${isDarkMode ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.9)'} !important`,
          }}
        />
      )}

      {/* Dust particles */}
      {stage === 'searching' && (
        <Box className="dust-container">
          {[...Array(24)].map((_, i) => (
            <Box
              key={i}
              className="dust-particle"
              sx={{
                '--angle': `${(i * 15)}deg`,
                '--delay': `${i * 0.03}s`,
                background: `radial-gradient(circle, ${isDarkMode ? 'rgba(255,255,255,1)' : 'rgba(0,0,0,1)'} 0%, ${isDarkMode ? 'rgba(255,255,255,0)' : 'rgba(0,0,0,0)'} 70%)`,
              }}
            />
          ))}
        </Box>
      )}

      {/* Found - Note reveal */}
      {stage === 'found' && (
        <Box className="note-reveal">
          <Box
            className="note-glow"
            sx={{
              animation: 'noteReveal 2s ease-out',
            }}
          >
            {result.noteType === 'tiered' && result.icon ? (
              <Box
                component="img"
                src={result.icon}
                alt={result.tier}
                sx={{ width: 64, height: 64, objectFit: 'contain' }}
              />
            ) : (
              <Box sx={{ fontSize: '48px' }}>📜</Box>
            )}
          </Box>
          <Typography
            variant="h6"
            className="found-text"
            sx={{
              color: isDarkMode ? '#ffffff !important' : '#000000 !important',
              fontFamily: 'Gochi Hand, cursive',
              textShadow: 'none !important',
            }}
          >
            Found {result.noteType === 'tiered' ? result.tier : result.rarity}!
          </Typography>
        </Box>
      )}

      {/* Not found */}
      {stage === 'notFound' && (
        <Box className="nothing-found">
          <Typography
            variant="h6"
            sx={{
              color: '#ffffff',
              fontFamily: 'Gochi Hand, cursive',
              fontWeight: 600,
              fontSize: '1.2rem',
            }}
          >
            Nothing here...
          </Typography>
        </Box>
      )}

      {/* Hidden audio elements */}
      <audio ref={searchingAudioRef} src={searchingSound} preload="auto" />
      <audio ref={foundAudioRef} src={foundSound} preload="auto" />
    </Box>
  );
}

export default SearchAnimation;
